import React, { useEffect, useState } from "react";
import axios from 'axios';
import Loading from 'react-fullscreen-loading';
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import DataTable from 'react-data-table-component';
import ShowTime from "../../../utils/ShowTime/ShowTime";
import MapProduct from "../../../utils/ProductMapper/MapProduct";
import { FaLaptopMedical, FaSearch } from "react-icons/fa";
import CustomLoader from "../../../utils/DataTableLoader/CustomLoader";
import UserDashboardLayout from "../../../Components/UserDashboardLayout/UserDashboardLayout";
import RenewButton from "../../../Components/RenewButton/RenewButton";
import DateRangeFilter from "../../../Components/DateRangeFilter/DateRangeFilter";
import { Tooltip } from 'react-tooltip';
import { FiLock, FiUnlock } from "react-icons/fi";
import "./License.css";

// Licenses with no product attached are not missing data — they run on ALL
// products, deliberately. An empty product already means "do not filter", so
// asking for exactly those needs a sentinel. Mirrors NO_PRODUCT in the backend's
// routes/website/user/license.js; the two must stay in step.
const NO_PRODUCT = "__none__";

// Derive a human-friendly status from expires_at (matches the dashboard's 3-day rule).
const getExpiryStatus = (expiresAt) => {
  if (!expiresAt) return { label: "-", className: "" };
  const days = Math.ceil((new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24));
  if (days <= 0) return { label: "Expired", className: "expired" };
  if (days <= 3) return { label: "Expiring Soon", className: "expiring" };
  return { label: "Active", className: "active" };
};

export default function Licenses() {
  const navigate = useNavigate();
  const [screenloader, setScreenloader] = useState(true);
  const [TableData, setTableData] = useState([]);
  const [TotalRecords, setTotalRecords] = useState();
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [pending, setPending] = useState(true);

  // Filters
  const [productOptions, setProductOptions] = useState([]);
  const [hasUniversal, setHasUniversal] = useState(false);
  const [product, setProduct] = useState("");
  const [status, setStatus] = useState("");
  const [locked, setLocked] = useState("");
  const [createdRange, setCreatedRange] = useState([null, null]);
  const [expiresRange, setExpiresRange] = useState([null, null]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  // Rows ticked for a bulk renewal. A renewal request now covers any number of
  // licenses, so picking several here and sending one request is the point —
  // it replaces visiting the quote screen once per license.
  const [selectedRows, setSelectedRows] = useState([]);
  // react-data-table clears its own checkboxes only when this toggles, so it is
  // flipped after a bulk action to reset them.
  const [clearSelection, setClearSelection] = useState(false);

  // Selection is per page, and react-data-table clears it itself when a
  // server-paginated table changes page — so the bar below disappears rather
  // than silently carrying a stale count into a renewal.
  //
  // Its `paginationServerOptions.persistSelectedOnPageChange` would keep
  // selections across pages, but the library removes the header select-all
  // checkbox when that is on (it is ambiguous once selections outlive the
  // page). Ticking twenty boxes by hand to renew twenty licenses is the worse
  // of the two, so select-all stays and paging starts fresh.
  const handleSelectionChange = ({ selectedRows: rows }) => setSelectedRows(rows);

  const renewLicenses = (licenses) => {
    const list = (licenses || []).filter(Boolean);
    if (!list.length) return;
    navigate(`/User/RequestQuote`, { state: { extendLicenses: list } });
  };

  const createdFrom = createdRange[0] ? createdRange[0].toISOString() : "";
  const createdTo = createdRange[1] ? createdRange[1].toISOString() : "";
  const expiresFrom = expiresRange[0] ? expiresRange[0].toISOString() : "";
  const expiresTo = expiresRange[1] ? expiresRange[1].toISOString() : "";

  const hasActiveFilters =
    product || status || locked || search || createdRange[0] || expiresRange[0];

  const clearFilters = () => {
    setProduct("");
    setStatus("");
    setLocked("");
    setCreatedRange([null, null]);
    setExpiresRange([null, null]);
    setSearchInput("");
    setSearch("");
  };

  // Debounce the search box so we don't fire a request on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset paging whenever a filter changes.
  useEffect(() => {
    setPage(1);
  }, [product, status, locked, createdFrom, createdTo, expiresFrom, expiresTo]);

  // Populate the product dropdown with only the products the user actually owns.
  useEffect(() => {
    const getProducts = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/user/dashboard/stats`, {
          headers: { "access-token": localStorage.getItem('accessToken') },
        });
        if (response.data.status) {
          const groups = response.data.stats.products || [];
          // The stats endpoint groups by product, so licenses with no product
          // arrive as their own group with product === null. Rendered naively
          // that became <option value={null}>-</option>, and because React drops
          // a null value attribute the browser fell back to the label — sending
          // product=- to the API, which matched nothing. Split the group out and
          // give it a real value.
          setProductOptions(groups.map((g) => g.product).filter(Boolean));
          setHasUniversal(groups.some((g) => !g.product));
        }
      } catch (error) {
        // Non-critical: the product filter just stays empty.
      }
    };
    getProducts();
  }, []);

  const columns = [
    {
      // Renewing used to be an unlabelled calendar icon that only explained
      // itself on hover — invisible on touch, and indistinguishable from the
      // machines icon next to it. It is now a named button, and it goes solid
      // on the rows that actually need it.
      name: "Actions",
      sortable: false,
      minWidth: '190px',
      cell: row => (
        <div className="license-actions">
          <RenewButton expiresAt={row.expires_at} onClick={() => renewLicenses([row])} />
          <FaLaptopMedical
            data-tooltip-id="my-tooltip"
            data-tooltip-content="view attached machines"
            data-tooltip-place="bottom"
            className="action-icon"
            onClick={() => navigate(`/user/licenses/${row.license_reference_id}`)}
          />
        </div>
      )
    },
    {
      name: 'License Id',
      selector: row => row.license_reference_id,
      sortField: "l.license_reference_id",
      sortable: true,
      minWidth: '180px',
      cell: row => <div className="cell-text">{row.license_reference_id}</div>,
    },
    {
      name: 'Product',
      selector: row => row.product,
      sortField: "l.product",
      sortable: true,
      minWidth: '220px',
      cell: row => (
        <div className="cell-text">
          {row.product ? MapProduct(row.product) : "Universal"}
        </div>
      ),
    },
    {
      name: 'In Use',
      selector: row => row.locked,
      sortField: "l.locked",
      sortable: true,
      minWidth: '180px',
      cell: row => (
        <div className="status-wrapper">
          {row.locked ? (
            <span className="status-badge in-use">
              <FiLock className="status-icon" /> In Use
            </span>
          ) : (
            <span className="status-badge available">
              <FiUnlock className="status-icon" /> Available
            </span>
          )}
        </div>
      ),
    },
    {
      name: 'Last User',
      selector: row => row.machine_user,
      sortField: "am.machine_user",
      sortable: true,
      minWidth: '150px',
      cell: row => <div className="cell-text">{row.machine_user || "-"}</div>,
    },
    {
      name: 'Status',
      selector: row => row.expires_at,
      sortField: "l.expires_at",
      sortable: true,
      minWidth: '150px',
      cell: row => {
        const s = getExpiryStatus(row.expires_at);
        return <span className={`expiry-pill ${s.className}`}>{s.label}</span>;
      },
    },
    {
      name: 'Last Seen',
      selector: row => row.last_seen,
      sortField: "am.last_seen",
      sortable: true,
      minWidth: '180px',
      cell: row => <div className="cell-text">{row.last_seen ? ShowTime(row.last_seen) : "-"}</div>,
    },
    {
      name: 'Created at',
      selector: row => row.created_at,
      sortField: "l.created_at",
      sortable: true,
      minWidth: '180px',
      cell: row => <div className="cell-text">{ShowTime(row.created_at)}</div>,
    },
    {
      name: 'Expires at',
      selector: row => row.expires_at,
      sortField: "l.expires_at",
      sortable: true,
      minWidth: '180px',
      cell: row => <div className="cell-text">{ShowTime(row.expires_at)}</div>,
    },
  ];

  const getList = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/user/license/index`, {
        params: {
          limit: pageSize,
          page,
          sort_by: sortBy,
          sort_order: sortOrder,
          product: product || undefined,
          status: status || undefined,
          search: search || undefined,
          locked: locked || undefined,
          created_from: createdFrom || undefined,
          created_to: createdTo || undefined,
          expires_from: expiresFrom || undefined,
          expires_to: expiresTo || undefined,
        },
        headers: { "access-token": localStorage.getItem('accessToken') },
      });
      setScreenloader(false);
      setPending(false);
      if (response.data.status) {
        setTotalRecords(response.data.meta.pagination.total);
        setTableData(response.data.licenses);
      } else {
        toast.error('Something Went Wrong');
      }
    } catch (error) {
      setScreenloader(false);
      setPending(false);
      toast.error(error.response?.data?.message || 'Something Went Wrong');
    }
  };

  useEffect(() => {
    setPending(true);
    getList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, sortBy, sortOrder, product, status, search, locked, createdFrom, createdTo, expiresFrom, expiresTo]);

  return (
    <div>
      <UserDashboardLayout />
      <Loading loading={screenloader} background="#fff" loaderColor="#883484" />
      <div className="content-page-bg">
        <div className="content-page">
          <div className="content">
            <div className="row">
              <h2>Licenses</h2>
            </div>

            <div className="row d-flex d-sm-none">
              <p>Total Records: <strong>{TotalRecords}</strong></p>
            </div>

            <div className="licenses-toolbar">
              <div className="search-box">
                <FaSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Search by license id…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
              {(productOptions.length > 0 || hasUniversal) && (
                <select
                  className="filter-select"
                  value={product}
                  onChange={(e) => setProduct(e.target.value)}
                >
                  <option value="">All products</option>
                  {productOptions.map((p) => (
                    <option key={p} value={p}>{MapProduct(p, "text")}</option>
                  ))}
                  {hasUniversal && (
                    <option value={NO_PRODUCT}>Universal licenses</option>
                  )}
                </select>
              )}
              <select
                className="filter-select"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="">All statuses</option>
                <option value="active">Active</option>
                <option value="expiring">Expiring Soon</option>
                <option value="expired">Expired</option>
              </select>
              {/* <select
                className="filter-select"
                value={locked}
                onChange={(e) => setLocked(e.target.value)}
              >
                <option value="">In use: any</option>
                <option value="1">In Use</option>
                <option value="0">Available</option>
              </select> */}
              <div className="date-filter">
                <DateRangeFilter
                  startDate={createdRange[0]}
                  endDate={createdRange[1]}
                  onChange={(dates) => setCreatedRange(dates)}
                  placeholderText="Created date"
                />
              </div>
              <div className="date-filter">
                <DateRangeFilter
                  startDate={expiresRange[0]}
                  endDate={expiresRange[1]}
                  onChange={(dates) => setExpiresRange(dates)}
                  placeholderText="Expiry date"
                />
              </div>
              {hasActiveFilters && (
                <button className="clear-filters-btn" onClick={clearFilters}>
                  Clear filters
                </button>
              )}
            </div>

            {/* Only present once something is ticked, so it never competes
                with the filters for attention on an untouched table. */}
            {selectedRows.length > 0 && (
              <div className="bulk-renew-bar">
                <span className="bulk-renew-count">
                  {selectedRows.length} license{selectedRows.length === 1 ? "" : "s"} selected
                </span>
                <div className="bulk-renew-actions">
                  <button
                    type="button"
                    className="bulk-renew-clear"
                    onClick={() => { setSelectedRows([]); setClearSelection(c => !c); }}
                  >
                    Clear
                  </button>
                  <RenewButton
                    urgent
                    label={`Renew ${selectedRows.length} on one invoice`}
                    onClick={() => renewLicenses(selectedRows)}
                  />
                </div>
              </div>
            )}

            {/* NOTE: do not add a `selectableRowSelected` prop to this table.
                react-data-table runs an effect keyed on
                [data, selectableRowSelected] that dispatches a selection
                update. An inline arrow gets a new identity on every render, so
                that effect fires on every render → onSelectedRowsChange →
                setState → re-render → forever. It hangs the browser tab. */}
            <div className="table-wrapper">
              <DataTable
                columns={columns}
                data={TableData}
                keyField="id"
                selectableRows
                onSelectedRowsChange={handleSelectionChange}
                clearSelectedRows={clearSelection}
                selectableRowsHighlight
                dense
                direction="auto"
                highlightOnHover
                noHeader
                persistTableHead
                pointerOnHover
                responsive
                progressPending={pending}
                progressComponent={<CustomLoader />}
                pagination
                paginationServer
                paginationPerPage={pageSize}
                paginationTotalRows={TotalRecords}
                paginationRowsPerPageOptions={[10, 20, 50, 100, 150]}
                onChangePage={setPage}
                onChangeRowsPerPage={(rows, currentPage) => { setPageSize(rows); setPage(currentPage); }}
                sortServer
                onSort={(col, dir) => { setSortBy(col.sortField); setSortOrder(dir); }}
              />
            </div>
          </div>
        </div>
      </div>
      <Tooltip id="my-tooltip" style={{ zIndex: 99 }} />
    </div>
  );
}
